import { normalizePubkey, npubEncode } from './keys.js';

/**
 * Domain binding -- the part that actually stops phishing.
 *
 * A key alone means nothing to a human: `npub1qq...` looks as much like a bank
 * as any other npub. Binding is therefore bidirectional:
 *
 *   1. the sender manifest (signed by the root key) claims a domain, and
 *   2. https://<domain>/.well-known/herald.json publishes the same root key.
 *
 * Only when both agree may a client show the domain as verified. One direction
 * alone is forgeable: anyone can claim a domain in a manifest, and anyone who
 * can host a file cannot thereby sign as the root key.
 */

export const WELL_KNOWN_PATH = '/.well-known/herald.json';

export function buildWellKnown({ root, name, relays = [], channels = {} }) {
  return {
    herald_version: 1,
    root: npubEncode(root),
    name,
    relays,
    channels,
  };
}

/**
 * @returns {Promise<{ok:true, doc:object}|{ok:false, error:string, doc?:object}>}
 */
export async function verifyDomainBinding({ domain, rootPubkey, fetchImpl = fetch, timeoutMs = 5000, origin = null }) {
  if (!domain) return { ok: false, error: 'no domain claimed' };
  if (!origin && !/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(domain)) return { ok: false, error: `implausible domain "${domain}"` };

  // `origin` exists only so the PoC can point at a local relay over http.
  // Production clients must never accept a non-https origin for a binding.
  const url = origin ? `${origin.replace(/\/$/, '')}${WELL_KNOWN_PATH}` : `https://${domain}${WELL_KNOWN_PATH}`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let doc;
  try {
    const res = await fetchImpl(url, { signal: controller.signal, redirect: 'error' });
    if (!res.ok) return { ok: false, error: `${url} returned HTTP ${res.status}` };
    doc = await res.json();
  } catch (err) {
    return { ok: false, error: `could not fetch ${url}: ${err.message}` };
  } finally {
    clearTimeout(timer);
  }

  if (!doc || typeof doc !== 'object') return { ok: false, error: 'well-known document is not an object' };
  let declared;
  try {
    declared = normalizePubkey(doc.root);
  } catch {
    return { ok: false, error: 'well-known document has no valid root key', doc };
  }
  if (declared !== normalizePubkey(rootPubkey)) {
    return { ok: false, error: `${domain} publishes a different root key than this sender claims`, doc };
  }
  return { ok: true, doc };
}
